import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import { Module } from 'modules/Module';
import React from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useDispatch, useSelector } from 'react-redux';
import { tabsChangedActive, tabsClosed, tabsReordered } from 'store/actions/tabs';
import { IAppState } from 'store/reducers/rootReducer';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReactCountryFlag from 'react-country-flag';
import { getModuleIcon } from './menu/utils';
import { LANGUAGE_FLAGS } from './menu/constants';
import { IModule } from 'types/module';

interface DraggableTabProps {
  tab: IModule;
  index: number;
  isActive: boolean;
  onClose: (event: React.MouseEvent<HTMLDivElement>, id: string) => void;
  onChange: (event: React.ChangeEvent<{}>, id: string) => void;
  moduleName: string;
  courseName: string | null;
  language: string | null;
  needsAttention: boolean;
}

const DraggableTab: React.FC<DraggableTabProps> = ({
  tab,
  index,
  isActive,
  onClose,
  onChange,
  moduleName,
  courseName,
  language,
  needsAttention,
}) => {
  const icon = getModuleIcon(moduleName);

  return (
    <Draggable draggableId={tab.id} index={index} disableInteractiveElementBlocking>
      {(draggableProvided, snapshot) => (
        <div
          ref={draggableProvided.innerRef}
          {...draggableProvided.draggableProps}
          {...draggableProvided.dragHandleProps}
        >
          <StyledTab
            value={tab.id}
            onChange={onChange}
            disableRipple
            disableFocusRipple
            $isActive={isActive}
            $isDragging={snapshot.isDragging}
            label={
              <TabLabel>
                <TabContent>
                  <TopLine>
                    <TabIcon>
                      <FontAwesomeIcon icon={icon} />
                    </TabIcon>
                    <ModuleName>{moduleName}</ModuleName>
                    {needsAttention && <AttentionMark title="Module opened" />}
                  </TopLine>
                  {courseName && language && (
                    <CourseInfo>
                      <FlagWrapper>
                        <ReactCountryFlag
                          countryCode={LANGUAGE_FLAGS[language] || 'XX'}
                          svg
                          style={{ width: '16px', height: '11px' }}
                        />
                      </FlagWrapper>
                      <CourseName>{courseName}</CourseName>
                    </CourseInfo>
                  )}
                </TabContent>
                <CloseButton onClick={(event) => onClose(event, tab.id)} $isActive={isActive}>
                  ×
                </CloseButton>
              </TabLabel>
            }
          />
        </div>
      )}
    </Draggable>
  );
};

export const TabsDashboard: React.FC = () => {
  const { tabs, activeTabId, moduleNames, pendingTabIds } = useSelector((state: IAppState) => state.tabs);
  const dispatch = useDispatch();

  React.useEffect(() => {
    if (activeTabId && pendingTabIds[activeTabId]) {
      dispatch(tabsChangedActive(activeTabId));
    }
  }, [activeTabId, pendingTabIds, dispatch]);

  const handleChange = (_event: React.ChangeEvent<{}>, id: string) => {
    dispatch(tabsChangedActive(id));
  };

  const handleCloseClick = (event: React.MouseEvent<HTMLDivElement>, id: string) => {
    event.stopPropagation();
    dispatch(tabsClosed(id));
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    dispatch(tabsReordered({ sourceIndex: result.source.index, destinationIndex: result.destination.index }));
  };

  const getTabInfo = (tab: IModule) => {
    const tabId = tab.id || 'Unknown';

    if (tab.courseId === 0) {
      return { moduleName: tabId, courseName: null, language: null };
    }

    const fullPath = moduleNames[`${tab.courseId}-${tab.refId}`] || tabId;
    const pathParts = fullPath.split(' - ');
    const moduleName = pathParts[pathParts.length - 1];

    if (pathParts.length >= 3) {
      return { moduleName, courseName: pathParts[1], language: pathParts[0] };
    }

    if (pathParts.length >= 2 && pathParts[0] === 'Vocabulary (main)' && pathParts.length >= 4) {
      return { moduleName, courseName: pathParts[2], language: pathParts[1] };
    }

    return { moduleName, courseName: null, language: null };
  };

  return (
    <Container>
      <TabsContainer>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tabs" direction="horizontal">
            {(droppableProvided) => (
              <div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                <TabsStyled value={activeTabId ?? false} onChange={handleChange} variant="scrollable" scrollButtons="auto">
                  {tabs.map((tab, index) => {
                    const { moduleName, courseName, language } = getTabInfo(tab);
                    const needsAttention = tab.id !== activeTabId && !!pendingTabIds[tab.id];
                    const isActive = tab.id === activeTabId;

                    return (
                      <DraggableTab
                        key={tab.id}
                        tab={tab}
                        index={index}
                        isActive={isActive}
                        onClose={handleCloseClick}
                        onChange={handleChange}
                        moduleName={moduleName}
                        courseName={courseName}
                        language={language}
                        needsAttention={needsAttention}
                      />
                    );
                  })}
                  {droppableProvided.placeholder}
                </TabsStyled>
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </TabsContainer>
      <ModulesContainer>
        {tabs.map((tab) => (
          <Module module={tab} key={tab.id} isActive={tab.id === activeTabId} />
        ))}
      </ModulesContainer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
`;

const TabsContainer = styled.div`
  background-color: #4a4a4a;
  z-index: 1;
  border-left: 2px solid #4a4a4a;
  border-top: 4px solid #4a4a4a;
`;

const TabsStyled = styled(Tabs)`
  min-height: 40px;

  & .MuiTabs-root {
    min-height: 40px;
  }

  & .MuiTabs-indicator {
    display: none;
  }

  & .MuiTabs-scrollButtons {
    color: #969696;

    &.Mui-disabled {
      opacity: 0.3;
    }
  }

  & .MuiTab-root + .MuiTab-root {
    border-left: 2px solid #4a4a4a;
  }

  & .MuiTab-root:not(:last-child) {
    border-right: 2px solid #4a4a4a;
  }

  & * {
    box-shadow: none !important;
  }
`;

const StyledTab = styled(Tab)<{ $isActive: boolean; $isDragging?: boolean }>`
  min-height: 40px;
  min-width: unset;
  max-width: 300px;
  padding: 0;
  text-transform: none;
  color: ${(props) => (props.$isActive ? '#000000' : '#e0e0e0')} !important;
  background-color: ${(props) => (props.$isDragging ? '#3a3f50' : props.$isActive ? '#ffffff' : '#1a1f2e')} !important;
  opacity: 1;
  transition: all 0.15s ease;
  position: relative;
  box-shadow: none !important;
  flex-grow: 0;
  border-bottom: 1px solid transparent;
  border-left: 2px solid transparent;
  border-right: 2px solid transparent;
  ${(props) =>
    props.$isActive &&
    `
    border-bottom-color: #e0e0e0;
    border-bottom-style: dashed;
  `}

  &:hover {
    background-color: ${(props) => (props.$isActive ? '#ffffff' : '#2a2d2e')} !important;
    color: ${(props) => (props.$isActive ? '#000000' : '#ffffff')} !important;
  }

  &.MuiTab-root {
    min-width: unset;
    background-color: ${(props) => (props.$isDragging ? '#3a3f50' : props.$isActive ? '#ffffff' : '#1a1f2e')} !important;
    box-shadow: none !important;
    border-bottom: 1px solid transparent !important;
    ${(props) =>
      props.$isActive &&
      `
      border-bottom-color: #e0e0e0 !important;
      border-bottom-style: dashed !important;
    `}

    &:focus {
      box-shadow: none !important;
      outline: none !important;
    }

    &.Mui-selected {
      box-shadow: none !important;
      border-bottom-color: #e0e0e0 !important;
      border-bottom-style: dashed !important;
    }
  }

  & .MuiTab-wrapper {
    color: inherit;
  }

  & .MuiTouchRipple-root {
    display: none;
  }
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  padding: 0 8px 0 12px;
  gap: 6px;
  height: 40px;
`;

const TabIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  font-size: 13px;
  flex-shrink: 0;
  color: inherit;
  line-height: 1;
  margin-bottom: 2px;
`;

const TabContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  overflow: hidden;
`;

const TopLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
`;

const ModuleName = styled.span`
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1;
  text-transform: uppercase;
`;

const AttentionMark = styled.span`
  display: inline-flex;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-left: 4px;
  background-color: #ff4d4f;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
`;

const CourseInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 1;
`;

const FlagWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 11px;
  flex-shrink: 0;
`;

const CourseName = styled.span`
  font-size: 11px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0.8;
`;

const CloseButton = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 4px;
  opacity: ${(props) => (props.$isActive ? 0.8 : 0)};
  transition: opacity 0.15s ease;
  border-radius: 2px;
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;

  ${StyledTab}:hover & {
    opacity: 0.8;
    visibility: visible;
  }

  &:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const ModulesContainer = styled.div`
  display: flex;
  flex-grow: 1;
  overflow: hidden;
  background-color: #ffffff;
`;
